import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  CreditCard,
  FileText,
  Settings,
  Mail,
  Shield,
  BarChart3,
  LogOut,
  Target,
  Award,
  Lock,
  ChevronRight,
  Calendar,
  Megaphone,
  Share2,
  Trophy
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const AdminSidebar = () => {
  const location = useLocation();
  const { admin, logout, hasPermission } = useAdminAuth();

  const menuItems = [
    { 
      path: '/admin/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      permission: null,
      hiddenForRoles: ['affiliated_member']
    },
    { 
      path: '/admin/target-members', 
      icon: Target, 
      label: 'Target Members',
      permission: ['target_members', 'view']
    },
    {
      path: '/admin/affiliate-referrals',
      icon: Share2,
      label: 'Affiliate / Member Referral',
      permission: ['affiliate_referrals', 'view'],
      hiddenForRoles: ['affiliated_member']
    },
    { 
      path: '/admin/registrations', 
      icon: UserPlus, 
      label: 'Registrations',
      permission: ['members', 'validate']
    },
    { 
      path: '/admin/members', 
      icon: Users, 
      label: 'Members',
      permission: ['members', 'view']
    },
    { 
      path: '/admin/payments', 
      icon: CreditCard, 
      label: 'Payments',
      permission: ['payments', 'view']
    },
    { 
      path: '/admin/invoices', 
      icon: FileText, 
      label: 'Invoices',
      permission: ['invoices', 'view']
    },
    { 
      path: '/admin/tiers', 
      icon: Award, 
      label: 'Tier Config',
      permission: ['tiers', 'view']
    },
    { 
      path: '/admin/event-fees', 
      icon: Calendar, 
      label: 'Event Fees',
      permission: ['event_fees', 'view']
    },
    { 
      path: '/admin/announcements', 
      icon: Megaphone, 
      label: 'Announcements',
      permission: ['announcements', 'view']
    },
    {
      path: '/admin/member-stories',
      icon: FileText,
      label: 'Story Approvals',
      permission: ['announcements', 'view']
    },
    { 
      path: '/admin/restrictions', 
      icon: Lock, 
      label: 'Restrictions',
      permission: ['members', 'restrict']
    },
    { 
      path: '/admin/user-roles', 
      icon: Shield, 
      label: 'User Roles',
      permission: ['roles', 'view']
    },
    { 
      path: '/admin/users', 
      icon: Users, 
      label: 'Users',
      permission: ['users', 'view']
    },
    { 
      path: '/admin/templates', 
      icon: Mail, 
      label: 'Email Templates',
      permission: ['templates', 'view']
    },
    { 
      path: '/admin/reports', 
      icon: FileText, 
      label: 'Reports',
      permission: ['reports', 'view']
    },
    { 
      path: '/admin/activity-logs', 
      icon: BarChart3, 
      label: 'Activity Logs',
      permission: ['logs', 'view']
    },
    {
      path: '/admin/leaderboard',
      icon: Trophy,
      label: 'Member Leaderboard',
      permission: ['settings', 'view']
    },
    { 
      path: '/admin/settings', 
      icon: Settings, 
      label: 'Settings',
      permission: ['settings', 'view']
    },
  ];

  const visibleMenuItems = menuItems.filter(item => {
    if ((item.hiddenForRoles || []).includes(admin?.role)) return false;
    if (!item.permission) return true;
    return hasPermission(item.permission[0], item.permission[1]);
  });

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Super Admin' },
      admin: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Admin' },
      finance_admin: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Finance Admin' },
      affiliated_member: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Affiliated Member' }
    };
    return badges[role] || badges.finance_admin;
  };

  const roleBadge = getRoleBadge(admin?.role);

  return (
    <div className="h-screen w-64 bg-slate-900 fixed left-0 top-0 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">One Globe</h1>
            <p className="text-xs text-slate-400">Admin Portal</p>
          </div>
        </div>
      </div>

      {/* Admin Info */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm">
              {admin?.full_name?.charAt(0) || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{admin?.full_name}</p>
            <span className={`text-xs px-2 py-0.5 rounded ${roleBadge.bg} ${roleBadge.text}`}>
              {roleBadge.label}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-1">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {isActive && <ChevronRight size={16} className="ml-auto" />}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-slate-700">
        <button
          onClick={logout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
